import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAdminWeekly } from "@/hooks/use-admin-weekly";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { ReportDownloadButtons } from "@/components/reports/report-download-buttons";
import { AttendanceReportTable } from "@/components/reports/attendance-report-table";
import {
  buildPeriodReport,
  shiftWeek,
  weekLabel,
} from "@/lib/reports/build-report";
import { orderedAdminTables } from "@/lib/admin-tables";
import { todayIST } from "@/lib/timezone";

export default function WeeklyAttendancePage() {
  const [anchor, setAnchor] = useState(todayIST());
  const { data, dates, isLoading, error } = useAdminWeekly(anchor);

  const tables = useMemo(
    () => (data ? orderedAdminTables(data.tables) : []),
    [data]
  );

  const reports = useMemo(() => {
    if (!data) return [];
    return tables
      .map((table) => ({
        table,
        report: buildPeriodReport(data, dates, {
          period: "weekly",
          tableKey: table.key,
          title: `Weekly Presence — ${table.label}`,
          presenceHeader: "Weekly Presence",
        }),
      }))
      .filter((item) => item.report.rows.length > 0);
  }, [data, dates, tables]);

  const weekStart = dates[0] ?? anchor;
  const filenameBase = `weekly-presence-${weekStart}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Weekly Presence</h2>
          <p className="text-sm text-muted-foreground">
            Separate tables per group. Presence % = P ÷ (P+A+L). MS/FS is a
            label only — late and absent still count. O, S, TP excluded.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setAnchor(shiftWeek(anchor, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="min-w-[180px] text-center text-sm font-medium">
            {weekLabel(anchor)}
          </p>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setAnchor(shiftWeek(anchor, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <DatePicker value={anchor} onChange={setAnchor} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <Legend label="P Present" className="bg-[#00B050] text-white" />
        <Legend label="A Absent" className="bg-[#FF0000] text-white" />
        <Legend label="O Official leave" className="bg-[#7030A0] text-white" />
        <Legend label="S Sick" className="bg-[#ED7D31] text-white" />
        <Legend label="TP Permission" className="bg-[#00B0F0] text-white" />
        <Legend label="L Late" className="bg-[#5C1A1A] text-white" />
        <span>
          Example: 1×P + 1×L = <strong>50%</strong>; 5×TP = <strong>—</strong>
        </span>
      </div>

      {isLoading && (
        <p className="text-muted-foreground">Loading weekly presence…</p>
      )}
      {error && (
        <p className="text-red-600">Could not load weekly presence.</p>
      )}

      {reports.map(({ table, report }) => (
        <div key={table.key} className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold">{table.label}</h3>
            <ReportDownloadButtons
              report={report}
              filenameBase={`${filenameBase}-${table.key}`}
            />
          </div>
          <div className="overflow-x-auto rounded-lg border bg-white">
            <AttendanceReportTable report={report} />
          </div>
        </div>
      ))}

      {data && reports.length === 0 && (
        <p className="text-muted-foreground">
          No attendance rows for week of{" "}
          {format(parseISO(weekStart), "dd MMM yyyy")}.
        </p>
      )}
    </div>
  );
}

function Legend({ label, className }: { label: string; className: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`inline-flex h-4 min-w-[1.25rem] items-center justify-center rounded px-1 text-[10px] font-bold ${className}`}
      >
        {label.slice(0, label.indexOf(" ") > 0 ? label.indexOf(" ") : 1)}
      </span>
      {label.slice(label.indexOf(" ") + 1)}
    </span>
  );
}
